export function roomsTotalFromReservation(reservation) {
  const suits = reservation?.suits || reservation?.reservation?.suits || []
  return suits.reduce((sum, room) => sum + (Number(room.price) || 0), 0)
}

export function servicesTotalFromReservation(reservation) {
  const services = reservation?.services || []
  return services.reduce((sum, svc) => sum + (Number(svc.total_price) || 0), 0)
}

export function tariffTotalFromReservation(reservation) {
  if (!reservation) return 0
  return (
    Number(reservation.price) ||
    roomsTotalFromReservation(reservation) + servicesTotalFromReservation(reservation)
  )
}

export function familyDiscountPercent(reservation) {
  const fromField = Number(reservation?.family_discount)
  if (!Number.isNaN(fromField) && fromField > 0) return fromField

  const list = (reservation?.discount && reservation.discount.list) || []
  const item = list.find((line) => line.text === 'Семейная скидка')
  return item ? Number(item.name) || 0 : 0
}

export function promoDiscountAmount(reservation, tariff = null) {
  const tariffSum = tariff ?? tariffTotalFromReservation(reservation)
  const fromApi = Number(reservation?.price_promo_code)
  if (!Number.isNaN(fromApi) && fromApi > 0) return fromApi

  if (!reservation?.promo_code) return 0

  const percent = Number(reservation.promo_code.discount) || 0
  if (percent <= 0) return 0

  return Math.floor((tariffSum * percent) / 100 / 10) * 10
}

export function promoDiscountLabel(reservation) {
  if (reservation?.promo_code) {
    return `Промокод «${reservation.promo_code.name}» (${reservation.promo_code.discount}%)`
  }
  return 'Промокод'
}

export function localDiscountPercent(reservation) {
  let percent = Number(reservation?.additional_discount) || 0
  percent += familyDiscountPercent(reservation)

  if (Number(reservation?.payment_option) === 1) {
    percent += 5
  }

  return Math.min(percent, 100)
}

export function localPriceDiscount(reservation, tariff = null) {
  const tariffSum = tariff ?? tariffTotalFromReservation(reservation)
  const percent = localDiscountPercent(reservation)

  if (percent <= 0 || tariffSum <= 0) return 0

  return Math.floor((tariffSum * percent) / 100 / 10) * 10
}

export function percentDiscountAmount(reservation, tariff = null) {
  const tariffSum = tariff ?? tariffTotalFromReservation(reservation)
  const fromApi = Number(reservation?.price_discount)
  if (!Number.isNaN(fromApi) && fromApi > 0) return fromApi

  return localPriceDiscount(reservation, tariffSum)
}

function allocateDiscountParts(parts, totalAmount) {
  if (!parts.length) {
    return totalAmount > 0 ? [{ text: 'Скидки', amount: totalAmount }] : []
  }

  const weightSum = parts.reduce((sum, part) => sum + part.weight, 0)
  if (weightSum === 0) {
    return totalAmount > 0 ? [{ text: 'Скидки', amount: totalAmount }] : []
  }

  const lines = []
  let allocated = 0

  parts.forEach((part, index) => {
    const amount =
      index === parts.length - 1
        ? totalAmount - allocated
        : Math.floor(((totalAmount * part.weight) / weightSum / 10) * 10)
    allocated += amount

    if (amount > 0) {
      lines.push({ text: part.text, amount })
    }
  })

  return lines
}

export function percentDiscountLines(reservation, tariff = null) {
  const tariffSum = tariff ?? tariffTotalFromReservation(reservation)
  const totalDiscount = percentDiscountAmount(reservation, tariffSum)
  if (totalDiscount <= 0) return []

  const parts = []

  if (Number(reservation?.payment_option) === 1) {
    parts.push({ text: 'Оплата наличными (5%)', weight: 5 })
  }

  const familyPercent = familyDiscountPercent(reservation)
  if (familyPercent > 0) {
    parts.push({ text: 'Семейная скидка', weight: familyPercent })
  }

  if (Number(reservation?.additional_discount) > 0) {
    parts.push({
      text: 'Дополнительная скидка',
      weight: Number(reservation.additional_discount),
    })
  }

  return allocateDiscountParts(parts, totalDiscount)
}

export function calculatedPayPrice(reservation, tariff = null) {
  const tariffSum = tariff ?? tariffTotalFromReservation(reservation)

  if (reservation?.discount?.calculated_pay != null) {
    return Number(reservation.discount.calculated_pay)
  }

  if (reservation?.calculated_pay_price != null) {
    return Number(reservation.calculated_pay_price)
  }

  return (
    tariffSum -
    promoDiscountAmount(reservation, tariffSum) -
    percentDiscountAmount(reservation, tariffSum)
  )
}

export function manualAdjustmentAmount(reservation, tariff = null) {
  if (reservation?.manual_total == null || reservation.manual_total === '') return 0

  const fromApi = Number(reservation.manual_discount)
  if (!Number.isNaN(fromApi) && fromApi > 0) return fromApi

  const calculated = calculatedPayPrice(reservation, tariff)
  const payPrice = Number(reservation.pay_price ?? reservation.manual_total) || 0
  const diff = calculated - payPrice

  return diff > 0 ? diff : 0
}

export function finalPayPrice(reservation, tariff = null) {
  if (reservation?.manual_total != null && reservation.manual_total !== '') {
    return Number(reservation.manual_total)
  }

  if (reservation?.pay_price != null && reservation.manual_total == null) {
    return Number(reservation.pay_price)
  }

  return calculatedPayPrice(reservation, tariff)
}

export function hasPricingAdjustments(reservation, tariff = null) {
  const tariffSum = tariff ?? tariffTotalFromReservation(reservation)
  return (
    promoDiscountAmount(reservation, tariffSum) > 0 ||
    percentDiscountLines(reservation, tariffSum).length > 0 ||
    manualAdjustmentAmount(reservation, tariffSum) > 0
  )
}
